/* @jsx h */
import { defineComponent, h, ref, computed, PropType } from "vue";
import { Loading as LoadingComponent } from "../Loading";

export interface SelectableItem {
  id: number;
  name: string;
  subtitle?: string;
}

export type SelectableListComboBoxProps = {
  items: SelectableItem[];
  selectedIds: number[];
  onSelectionChange: (selectedIds: number[]) => void;
  loading?: boolean;
  placeholder?: string;
  emptyMessage?: string;
  selectionLabel?: string;
  defaultShowSelected?: boolean;
};

export const SelectableListComboBox =
  defineComponent<SelectableListComboBoxProps>({
    name: "SelectableListComboBox",
    props: {
      items: {
        type: Array as PropType<SelectableItem[]>,
        required: true,
      },
      selectedIds: {
        type: Array as PropType<number[]>,
        required: true,
      },
      onSelectionChange: {
        type: Function as PropType<(selectedIds: number[]) => void>,
        required: true,
      },
      loading: {
        type: Boolean,
        default: false,
      },
      placeholder: {
        type: String,
        default: "Search items...",
      },
      emptyMessage: {
        type: String,
        default: "No items found",
      },
      selectionLabel: {
        type: String,
        default: "item(s) selected",
      },
      defaultShowSelected: {
        type: Boolean,
        default: false,
      },
    },
    setup(props) {
      const searchQuery = ref("");
      const showSelected = ref(props.defaultShowSelected);

      // Sort items alphabetically by name
      const sortedItems = computed(() => {
        return [...props.items].sort((a, b) => a.name.localeCompare(b.name));
      });

      // Filter items based on search and selected filter
      const filteredItems = computed(() => {
        let filtered = sortedItems.value;

        // Filter by search query
        if (searchQuery.value.trim()) {
          const query = searchQuery.value.toLowerCase().trim();
          filtered = filtered.filter((item) =>
            item.name.toLowerCase().includes(query),
          );
        }

        // Filter by selected status
        if (showSelected.value) {
          filtered = filtered.filter((item) =>
            props.selectedIds.includes(item.id),
          );
        }

        return filtered;
      });

      const toggleItem = (itemId: number) => {
        const newSelectedIds = props.selectedIds.includes(itemId)
          ? props.selectedIds.filter((id) => id !== itemId)
          : [...props.selectedIds, itemId];
        props.onSelectionChange(newSelectedIds);
      };

      const selectAll = () => {
        const allVisibleIds = filteredItems.value.map((item) => item.id);
        const newSelectedIds = [
          ...new Set([...props.selectedIds, ...allVisibleIds]),
        ];
        props.onSelectionChange(newSelectedIds);
      };

      const selectNone = () => {
        const visibleIds = new Set(filteredItems.value.map((item) => item.id));
        const newSelectedIds = props.selectedIds.filter(
          (id) => !visibleIds.has(id),
        );
        props.onSelectionChange(newSelectedIds);
      };

      const toggleShowSelected = () => {
        showSelected.value = !showSelected.value;
      };

      return () => (
        <div class="space-y-3">
          {/* Search and Filter Controls */}
          <div class="flex flex-col sm:flex-row gap-2">
            {/* Search Input */}
            <div class="flex-1 relative">
              <input
                type="text"
                value={searchQuery.value}
                onInput={(e) =>
                  (searchQuery.value = (e.target as HTMLInputElement).value)
                }
                placeholder={props.placeholder}
                class="input input-bordered input-sm w-full pl-10"
              />
              <span class="material-symbols-outlined absolute left-3 top-1/2 transform -translate-y-1/2 text-base-content/60 text-lg">
                search
              </span>
            </div>

            {/* Control Buttons */}
            <div class="flex gap-1">
              {/* Show Selected/All Toggle */}
              <button
                type="button"
                onClick={toggleShowSelected}
                class={`btn btn-sm gap-1 ${
                  showSelected.value ? "btn-primary" : "btn-secondary"
                }`}
                title={
                  showSelected.value ? "Show all items" : "Show selected only"
                }
              >
                <span class="material-symbols-outlined text-base">
                  {showSelected.value ? "checklist" : "view_list"}
                </span>
              </button>

              {/* Select All */}
              <button
                type="button"
                onClick={selectAll}
                class="btn btn-sm btn-secondary gap-1"
                title="Select all visible items"
                disabled={filteredItems.value.length === 0}
              >
                <span class="material-symbols-outlined text-base">
                  select_all
                </span>
              </button>

              {/* Select None */}
              <button
                type="button"
                onClick={selectNone}
                class="btn btn-sm btn-secondary gap-1"
                title="Unselect all visible items"
                disabled={filteredItems.value.length === 0}
              >
                <span class="material-symbols-outlined text-base">
                  deselect
                </span>
              </button>
            </div>
          </div>

          {/* Items List */}
          {props.loading ? (
            <div class="flex justify-center py-8">
              <LoadingComponent />
            </div>
          ) : (
            <div class="border border-base-300 rounded-lg bg-base-100">
              {filteredItems.value.length === 0 ? (
                <div class="p-6 text-center text-base-content/60">
                  {searchQuery.value.trim() || showSelected.value
                    ? "No items match your criteria"
                    : props.emptyMessage}
                </div>
              ) : (
                <div class="grid grid-cols-1 gap-0 max-h-64 overflow-y-auto">
                  {filteredItems.value.map((item, index) => (
                    <label
                      key={item.id}
                      class={`label cursor-pointer justify-start gap-3 p-3 hover:bg-base-200 transition-colors ${
                        index !== filteredItems.value.length - 1
                          ? "border-b border-base-300"
                          : ""
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={props.selectedIds.includes(item.id)}
                        onChange={() => toggleItem(item.id)}
                        class="checkbox checkbox-primary checkbox-sm"
                      />
                      <span class="label-text flex-1">
                        {item.name}
                        {item.subtitle ? (
                          <span class="text-base-content/60">
                            {" "}
                            &middot; {item.subtitle}
                          </span>
                        ) : null}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Selection Count */}
          <div class="text-sm text-base-content/60">
            {props.selectedIds.length} {props.selectionLabel}
          </div>
        </div>
      );
    },
  });
